import React from 'react'

import { Box, Divider, Text } from '@factorialco/deprecated-design-system'
import { sum } from 'lodash'

import Modal from 'modules/core/components/Modal'
import NumberField from 'modules/core/components/NumberField'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { FooterModalButtons } from 'modules/finance/components/ReconciliationForm/FooterModalButtons'
import { Reconcilable } from 'modules/finance/components/ReconciliationForm/types'

const areReconcilablesValid = (
  reconcilables: Reconcilable[],
  totalAmountCents: number,
  currency: string,
  locale: string
): string[] => {
  const errors: string[] = []

  const totalPaidSum = sum(reconcilables.map((reconcilable) => reconcilable.updatedAmountCents))

  if (totalPaidSum !== totalAmountCents) {
    const sum_partial_amounts = formattedAmount({
      amountCents: totalPaidSum,
      currency,
      locale: locale,
    })

    const amount = formattedAmount({
      amountCents: totalAmountCents,
      currency,
      locale: locale,
    })

    const options = {
      sum_partial_amounts,
      amount,
    }

    errors.push(i18n.t('finance.reconciliations.split_modal.errors.match_amount', options))
  }
  return errors
}

const CurrencyIndicator = ({ currency }: { currency: string }) => (
  <Text.Normal size="200" color="grey600" weight="medium">
    {currency.toUpperCase()}
  </Text.Normal>
)
interface Props {
  onClose: () => void
  onSubmit: (selectedReconcilables: Reconcilable[]) => void
  selectedReconcilables: Reconcilable[]
  totalAmountCents: number
  totalAmountCurrency: string
  locale: string
}

export const SplitReconciledAmountsModal = ({
  onClose,
  onSubmit,
  selectedReconcilables,
  totalAmountCents,
  totalAmountCurrency,
  locale,
}: Props) => {
  const [reconcilables, setReconcilables] = React.useState<Reconcilable[]>(selectedReconcilables)

  const [errors, setErrors] = React.useState<string[]>([])
  const [showErrors, setShowErrors] = React.useState<boolean>(false)
  const [forceInputValues, setForceInputValues] = React.useState<number>(0)

  const updateReconcilableAmount = (id: number, reconcilableAmountCents: number) => {
    let forceChange = false
    if (Math.round(reconcilableAmountCents * 100) / 100 !== reconcilableAmountCents) {
      forceChange = true
    }
    reconcilableAmountCents = Math.round(reconcilableAmountCents * 100)
    if (!reconcilableAmountCents || isNaN(reconcilableAmountCents) || reconcilableAmountCents < 0) {
      forceChange = true
      reconcilableAmountCents = 0
    }

    const newReconcilables = reconcilables.map((reconcilable) => {
      if (reconcilable.id === id) {
        if (reconcilableAmountCents > reconcilable.originalAmountCents) {
          forceChange = true
        }
        return {
          ...reconcilable,
          updatedAmountCents:
            reconcilableAmountCents > reconcilable.originalAmountCents
              ? reconcilable.originalAmountCents
              : reconcilableAmountCents,
        }
      }

      return reconcilable
    })
    setReconcilables([...newReconcilables])
    if (showErrors) {
      setErrors(
        areReconcilablesValid(newReconcilables, totalAmountCents, totalAmountCurrency, locale)
      )
    }
    if (forceChange) {
      setForceInputValues(forceInputValues + 1)
    }
  }

  const onSubmitInterceptor = () => {
    const errorList = areReconcilablesValid(
      reconcilables,
      totalAmountCents,
      totalAmountCurrency,
      locale
    )
    if (errorList.length === 0) {
      onSubmit(reconcilables)
    } else {
      setShowErrors(true)
      setErrors(errorList)
    }
  }

  const translations = {
    titleLabel: i18n.t('finance.reconciliations.split_modal.title'),
    descriptionLabel: i18n.t('finance.reconciliations.split_modal.subtitle'),
    reconciledLabel: i18n.t('finance.reconciliations.split_modal.reconciled'),
    amountLabel: i18n.t('finance.reconciliations.split_modal.amount'),
  }

  return (
    <Modal
      onClose={() => onClose()}
      title={translations.titleLabel}
      description={translations.descriptionLabel}
      illustration="alert"
      footer={<FooterModalButtons onCancel={() => onClose()} onConfirm={onSubmitInterceptor} />}
    >
      <Box flexDirection="row" width="full" gap="s6" paddingBottom="s4">
        <Box width="s200"></Box>
        <Box width="s140">
          <Text.Normal size="200" weight="semibold">
            {translations.reconciledLabel}
          </Text.Normal>
        </Box>
        <Box width="s6"></Box>
        <Box width="s140">
          <Text.Normal size="200" weight="semibold">
            {translations.amountLabel}
          </Text.Normal>
        </Box>
      </Box>
      <Box gap="s16">
        {reconcilables.map((reconcilable) => (
          <Box key={reconcilable.id} flexDirection="row" alignItems="center" gap="s6">
            <Box width="s200">
              <Box>
                <Text.Normal weight="semibold" size="200">
                  {reconcilable.description}
                </Text.Normal>
              </Box>
            </Box>
            <Box width="s140">
              <NumberField
                maxDecimals={2}
                step={0.01}
                min={0}
                max={reconcilable.originalAmountCents / 100}
                indicator={<CurrencyIndicator currency={reconcilable.currency.toUpperCase()} />}
                value={reconcilable.updatedAmountCents / 100}
                onChange={(value) => {
                  updateReconcilableAmount(reconcilable.id, value || 0)
                }}
              />
            </Box>
            <Box width="s6">/</Box>
            <Box width="s140" flexDirection="row" justifyContent="flexEnd">
              <NumberField
                readonly
                indicator={<CurrencyIndicator currency={reconcilable.currency.toUpperCase()} />}
                value={reconcilable.originalAmountCents / 100}
              />
            </Box>
          </Box>
        ))}
        <Box flexDirection="row" alignItems="center" gap="s6">
          <Box width="s200" flexNoShrink></Box>
          <Box width="full">
            <Divider />
          </Box>
        </Box>
        <Box flexDirection="row" alignItems="center" gap="s6">
          <Box width="s200"></Box>
          <Box width="s140">
            <NumberField
              readonly
              indicator={<CurrencyIndicator currency={totalAmountCurrency.toUpperCase()} />}
              value={totalAmountCents / 100}
            />
          </Box>
          <Box width="s6">/</Box>
          <Box width="s140" flexDirection="row" justifyContent="flexEnd">
            <NumberField
              readonly
              indicator={<CurrencyIndicator currency={totalAmountCurrency.toUpperCase()} />}
              value={
                sum(reconcilables.map((reconcilable) => reconcilable.originalAmountCents)) / 100
              }
            />
          </Box>
        </Box>
        <Box flexDirection="column" gap="s4">
          {errors.map((m, i) => (
            <Text.Normal size="100" key={i} color="radicalRed800">
              {m}
            </Text.Normal>
          ))}
        </Box>
      </Box>
    </Modal>
  )
}
